import { useState, useEffect } from 'react';
import { Send, Sparkles, Mail } from 'lucide-react';
import { useSendTemplates, useSendEmail, useDraftEmail, useRenderTemplate, useTestEmail } from '@/hooks/useSendTemplates';
import { Modal, Button, Textarea, Select, toast } from '@solid/ui';
import { VariableField } from '@/components/VariableField';
import { TEMPLATE_VARIABLES } from '@/lib/templateVariables';
import { useAuthStore } from '@/stores/auth.store';

interface SendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: { id: string; firstName: string; lastName: string; email?: string };
  dealId: string;
  dealName?: string;
}

const EMAIL_TYPES = [
  { value: 'intro_call', label: 'Intro Call Request' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'proposal_follow_up', label: 'Proposal Follow-Up' },
  { value: 'thank_you', label: 'Thank You' },
  { value: 'custom', label: 'Custom...' },
];

export function SendEmailModal({ open, onOpenChange, contact, dealId }: SendEmailModalProps) {
  const { data: templates = [] } = useSendTemplates();
  const sendEmail = useSendEmail();
  const draftEmail = useDraftEmail();
  const renderTemplate = useRenderTemplate();
  const testEmail = useTestEmail();
  const user = useAuthStore((s) => s.user);

  const [mode, setMode] = useState<'template' | 'ai' | 'compose'>('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [aiType, setAiType] = useState('intro_call');
  const [customPrompt, setCustomPrompt] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [renderedSubject, setRenderedSubject] = useState('');
  const [renderedBody, setRenderedBody] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (open) {
      setMode('template');
      setSelectedTemplateId('');
      setAiType('intro_call');
      setCustomPrompt('');
      setSubject('');
      setBody('');
      setRenderedSubject('');
      setRenderedBody('');
      setShowPreview(false);
    }
  }, [open]);

  useEffect(() => {
    if (selectedTemplateId && mode === 'template') {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) {
        setSubject(template.subject);
        setBody(template.body);
        // Render preview with actual data
        renderTemplate.mutate(
          { contactId: contact.id, dealId, templateId: selectedTemplateId },
          {
            onSuccess: (data) => {
              setRenderedSubject(data.subject);
              setRenderedBody(data.body);
              setShowPreview(true);
            },
          },
        );
      }
    }
  }, [selectedTemplateId, templates, mode, contact.id, dealId]);

  const handleGenerateAI = async () => {
    const result = await draftEmail.mutateAsync({
      contactId: contact.id,
      dealId,
      emailType: aiType,
      customPrompt: aiType === 'custom' ? customPrompt : undefined,
    });
    setSubject(result.subject);
    setBody(result.body);
    setRenderedSubject(result.subject);
    setRenderedBody(result.body);
    setMode('compose');
  };

  const handleSend = async () => {
    await sendEmail.mutateAsync({
      contactId: contact.id,
      dealId,
      templateId: mode === 'template' ? selectedTemplateId : undefined,
      subject,
      body,
    });
    onOpenChange(false);
  };

  const handleSendTest = async () => {
    try {
      await testEmail.mutateAsync({
        contactId: contact.id,
        dealId,
        templateId: mode === 'template' ? selectedTemplateId : undefined,
        subject,
        body,
      });
      toast.success(`Test email sent. Check your inbox at ${user?.email}`);
    } catch (err: any) {
      toast.error(err.message || 'Could not send test email.');
    }
  };

  if (!contact.email) {
    return (
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="No email address"
        footer={<Button onClick={() => onOpenChange(false)}>Close</Button>}
      >
        <p className="text-grey-500">This contact doesn't have an email address.</p>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={`Send Email to ${contact.firstName} ${contact.lastName}`}
      description={contact.email}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {((mode === 'template' && selectedTemplateId) || mode === 'compose') && (
              <Button
                variant="outline"
                onClick={handleSendTest}
                loading={testEmail.isPending}
                disabled={!subject || !body}
              >
                {testEmail.isPending ? (
                  'Sending...'
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-1" />
                    Send me a test
                  </>
                )}
              </Button>
            )}
          </div>
          {(mode === 'template' && selectedTemplateId) || mode === 'compose' ? (
            <Button onClick={handleSend} loading={sendEmail.isPending} disabled={!subject || !body}>
              {sendEmail.isPending ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-1" />
                  Send Email
                </>
              )}
            </Button>
          ) : null}
        </div>
      }
    >
      {/* Mode Selection */}
      {mode !== 'compose' && (
        <div className="flex gap-2 mb-4">
          <Button
            variant={mode === 'template' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setMode('template')}
          >
            Use Template
          </Button>
          <Button
            variant={mode === 'ai' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setMode('ai')}
          >
            <Sparkles className="w-4 h-4 mr-1" />
            AI Draft
          </Button>
        </div>
      )}

      {mode === 'template' && (
        <div className="space-y-4">
          <Select
            label="Template"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            options={[
              { value: '', label: 'Select a template...' },
              ...templates.map((t) => ({ value: t.id, label: t.name })),
            ]}
          />

          {selectedTemplateId && (
            <>
              {/* Preview (rendered with real data) */}
              {showPreview && (
                <div className="rounded-lg bg-grey-50 border border-grey-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-grey-500 uppercase tracking-wide">Preview</p>
                    <button
                      type="button"
                      onClick={() => setShowPreview(false)}
                      className="text-xs text-primary hover:underline"
                    >
                      Edit raw
                    </button>
                  </div>
                  <p className="text-sm font-medium text-secondary mb-1">{renderedSubject}</p>
                  <div className="text-sm text-grey-700 whitespace-pre-wrap">{renderedBody}</div>
                </div>
              )}

              {/* Raw editor (shown when not previewing) */}
              {!showPreview && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-grey-700 mb-1">Subject</label>
                    <VariableField value={subject} onChange={setSubject} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-grey-700 mb-1">Body</label>
                    <VariableField multiline rows={10} value={body} onChange={setBody} />
                  </div>
                  <VariableChips
                    onInsert={(key) => setBody((b) => b + key)}
                  />
                </>
              )}
            </>
          )}
        </div>
      )}

      {mode === 'ai' && (
        <div className="space-y-4">
          <Select
            label="Email Type"
            value={aiType}
            onChange={(e) => setAiType(e.target.value)}
            options={EMAIL_TYPES}
          />

          {aiType === 'custom' && (
            <Textarea
              label="What should this email accomplish?"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="e.g., Ask about their timeline and budget constraints..."
              rows={3}
            />
          )}

          <Button onClick={handleGenerateAI} loading={draftEmail.isPending}>
            {draftEmail.isPending ? (
              'Generating...'
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1" />
                Generate Draft
              </>
            )}
          </Button>
        </div>
      )}

      {mode === 'compose' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Subject</label>
            <VariableField value={subject} onChange={setSubject} />
          </div>
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Body</label>
            <VariableField multiline rows={10} value={body} onChange={setBody} />
          </div>
          <VariableChips onInsert={(key) => setBody((b) => b + key)} />
        </div>
      )}
    </Modal>
  );
}

function VariableChips({ onInsert }: { onInsert: (key: string) => void }) {
  return (
    <div className="rounded-lg border border-grey-200 bg-grey-50 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-grey-500 uppercase tracking-wide">
          Available Variables
        </p>
        <p className="text-xs text-grey-400">
          Type <span className="font-mono">{'{{'}</span> in the body for autocomplete
        </p>
      </div>
      <div className="space-y-2">
        {(['Contact', 'Deal', 'User'] as const).map((group) => (
          <div key={group}>
            <p className="text-[10px] font-medium text-grey-400 uppercase tracking-wide mb-1">
              {group}
            </p>
            <div className="flex flex-wrap gap-1">
              {TEMPLATE_VARIABLES.filter((v) => v.group === group).map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => onInsert(v.key)}
                  title={v.key}
                  className="px-2 py-1 text-xs bg-white border border-grey-200 hover:border-primary hover:text-primary rounded text-grey-700"
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
